import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mic, Square, Loader2 } from "lucide-react";

const MAX_SECONDS = 60;

export function VoiceNote({ onTranscript }) {
  const [state, setState] = useState("idle"); // idle | recording | transcribing
  const [seconds, setSeconds] = useState(0);
  const recorder = useRef(null);
  const chunks = useRef([]);
  const timer = useRef(null);
  const supported = typeof navigator !== "undefined" && !!navigator.mediaDevices && typeof MediaRecorder !== "undefined";

  useEffect(() => () => { if (timer.current) clearInterval(timer.current); }, []);

  const stop = () => {
    if (recorder.current && recorder.current.state !== "inactive") recorder.current.stop();
    if (timer.current) clearInterval(timer.current);
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunks.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setState("transcribing");
        try {
          const blob = new Blob(chunks.current, { type: rec.mimeType || "audio/webm" });
          const form = new FormData();
          form.append("audio", blob, "note.webm");
          const { data } = await api.post("/voice/transcribe", form);
          if (data.text) {
            onTranscript(data.text);
            toast.success("Heard you. Read it over before saving.");
          } else {
            toast.error("Nothing came through — try once more, a little closer.");
          }
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail));
        } finally {
          setState("idle");
          setSeconds(0);
        }
      };
      recorder.current = rec;
      rec.start();
      setState("recording");
      setSeconds(0);
      timer.current = setInterval(() => {
        setSeconds((s) => {
          if (s + 1 >= MAX_SECONDS) { stop(); return MAX_SECONDS; }
          return s + 1;
        });
      }, 1000);
    } catch {
      toast.error("Meridia needs microphone permission to listen.");
    }
  };

  if (!supported) return null;

  return (
    <div className="flex items-center gap-3" data-testid="voice-note">
      {state === "recording" && (
        <span className="text-sm text-[#C26D53] tabular-nums flex items-center gap-2" data-testid="voice-note-timer">
          <span className="w-2 h-2 rounded-full bg-[#C26D53] animate-pulse" /> {seconds}s / {MAX_SECONDS}s
        </span>
      )}
      {state === "transcribing" && (
        <span className="text-sm text-[#8C7A70] flex items-center gap-2" data-testid="voice-note-transcribing">
          <Loader2 size={14} className="animate-spin" /> listening back…
        </span>
      )}
      <button
        type="button"
        onClick={state === "recording" ? stop : start}
        disabled={state === "transcribing"}
        className="btn-ghost inline-flex items-center gap-2"
        data-testid={state === "recording" ? "voice-note-stop" : "voice-note-start"}
      >
        {state === "recording" ? <Square size={14} /> : <Mic size={14} />}
        {state === "recording" ? "Done" : "Murmur it instead"}
      </button>
    </div>
  );
}
